import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { CheckSquare, X } from 'lucide-react';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Button } from '@/shared/components/ui/Button';
import { QuestionShell } from '../QuestionShell';
import { describeStrings } from '../../utils/describe.strings';
import { findGoalMatch } from '../../utils/goalSuggestions';
import type { GoalAnswer, MockGoalTicket } from '../../types/describe.types';

interface GoalQuestionProps {
  status: 'active' | 'answered' | 'upcoming';
  isLast: boolean;
  answer: GoalAnswer | null;
  onSubmit: (answer: GoalAnswer) => void;
}

export const GoalQuestion = ({
  status,
  isLast,
  answer,
  onSubmit,
}: GoalQuestionProps) => {
  if (status === 'answered' && answer) {
    return (
      <QuestionShell
        kind="core"
        stepNumber={1}
        heading={`1. ${describeStrings.goal.heading}`}
        status="answered"
        isLast={isLast}
      >
        <div className="space-y-3 max-w-md">
          {answer.matchedTicket && !answer.dismissedMatch && (
            <MatchedTicketCard ticket={answer.matchedTicket} />
          )}
          {answer.description && (
            <p className="text-sm text-slate-700 leading-relaxed">
              {answer.description}
            </p>
          )}
        </div>
      </QuestionShell>
    );
  }

  return (
    <QuestionShell
      kind="core"
      stepNumber={1}
      heading={describeStrings.goal.heading}
      subheading={describeStrings.goal.subheading}
      status={status}
      isLast={isLast}
    >
      <GoalEditor
        initial={answer?.description ?? ''}
        initialDismissed={answer?.dismissedMatch ?? false}
        onSubmit={onSubmit}
      />
    </QuestionShell>
  );
};

interface GoalEditorProps {
  initial: string;
  initialDismissed: boolean;
  onSubmit: (answer: GoalAnswer) => void;
}

function GoalEditor({ initial, initialDismissed, onSubmit }: GoalEditorProps) {
  const [description, setDescription] = useState(initial);
  const [dismissedMatch, setDismissedMatch] = useState(initialDismissed);
  const [debouncedDescription] = useDebounce(description, 250);

  const suggestedTicket = useMemo<MockGoalTicket | null>(
    () => findGoalMatch(debouncedDescription),
    [debouncedDescription]
  );

  useEffect(() => {
    setDismissedMatch(false);
  }, [suggestedTicket?.key]);

  const submit = (attach: boolean) => {
    if (!description.trim()) return;
    onSubmit({
      description: description.trim(),
      matchedTicket: attach && suggestedTicket ? suggestedTicket : null,
      dismissedMatch: !attach,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      submit(Boolean(suggestedTicket) && !dismissedMatch);
    }
  };

  const showMatch = Boolean(suggestedTicket) && !dismissedMatch;

  return (
    <div className="space-y-3">
      <Textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={describeStrings.goal.placeholder}
        rows={3}
        className="w-full bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-900 resize-none shadow-sm focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:border-primary/60"
        autoFocus
      />

      {showMatch && suggestedTicket && (
        <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
            {describeStrings.goal.matchHeading}
          </div>
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center shrink-0">
              <CheckSquare className="h-3.5 w-3.5 text-blue-600" aria-hidden />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                {suggestedTicket.key}
              </div>
              <div className="text-sm font-medium text-slate-900 truncate">
                {suggestedTicket.title}
              </div>
            </div>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wide">
              {suggestedTicket.status}
            </span>
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              type="button"
              size="sm"
              onClick={() => submit(true)}
              disabled={!description.trim()}
            >
              {describeStrings.goal.matchAccept}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setDismissedMatch(true)}
            >
              <X className="h-3.5 w-3.5 me-1" aria-hidden />
              {describeStrings.goal.matchDismiss}
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">
          {describeStrings.goal.submitHint}
        </p>
        <Button
          type="button"
          onClick={() => submit(false)}
          disabled={!description.trim()}
        >
          {describeStrings.common.continue}
        </Button>
      </div>
    </div>
  );
}

function MatchedTicketCard({ ticket }: { ticket: MockGoalTicket }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 shadow-sm rounded-xl">
      <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center shrink-0">
        <CheckSquare className="h-3.5 w-3.5 text-blue-600" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
          {ticket.key}
        </div>
        <div className="text-sm font-medium text-slate-900 truncate">
          {ticket.title}
        </div>
      </div>
      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wide">
        {ticket.status}
      </span>
    </div>
  );
}
